import { useCallback, useEffect, useMemo } from 'react';

import { atom, useAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';

const locationAtom = atom<ReturnType<typeof useLocation> | undefined>(undefined);

const useCampaignManagementNavigation = ({
  onSavedLocationEmpty,
}: {
  /**
   * This callback is called when the management modal is closed and the background location is empty.
   * This will call the callback even if the management modal is not opened.
   */
  onSavedLocationEmpty?: () => void;
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [_location, setLocation] = useAtom(locationAtom);

  const managementLocation = useMemo(() => _location || location, [_location, location]);

  /** Opens the campaign management modal and preserves the location so that closing the modal navigates back to the correct location */
  const openCampaignManagementModal = useCallback(
    (route: string) => {
      setLocation(location);
      navigate(route);
    },
    [location, navigate, setLocation]
  );

  const navToBackground = useCallback(() => {
    setLocation(undefined);

    return _location ? navigate(_location.pathname) : navigate('..');
  }, [setLocation, _location, navigate]);

  useEffect(() => {
    if (_location === undefined && onSavedLocationEmpty) {
      onSavedLocationEmpty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_location]);

  return {
    backgroundLocation: managementLocation,
    savedLocation: _location,
    navigate,
    navToBackground,
    openCampaignManagementModal,
    /** @deprecated use openCampaignManagementModal instead - this name sucks */
    setBackgroundLocation: openCampaignManagementModal,
  };
};

export { useCampaignManagementNavigation };
